import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, X } from "lucide-react";
import { serviceDisplayName } from "@/lib/services";

// services = servizi di sistema ATTIVI (sola lettura, checkbox). appalto opzionale (edit).
export function AppaltoDialog({ services, appalto, onClose, onSaved }) {
  const isEdit = !!appalto;
  const [nome, setNome] = useState(appalto?.nome || "");
  const [descrizione, setDescrizione] = useState(appalto?.descrizione || "");
  const [emails, setEmails] = useState(appalto?.email_riferimento?.length ? [...appalto.email_riferimento] : [""]);
  const [selected, setSelected] = useState(new Set(appalto?.servizio_ids_abilitati || []));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const setEmailAt = (i, v) => setEmails((prev) => prev.map((e, idx) => (idx === i ? v : e)));
  const addEmail = () => setEmails((prev) => [...prev, ""]);
  const removeEmail = (i) => setEmails((prev) => (prev.length <= 1 ? [""] : prev.filter((_, idx) => idx !== i)));
  const cleanEmails = () => {
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    const seen = new Set(); const out = [];
    for (const raw of emails) {
      const v = (raw || "").trim();
      if (!v) continue;
      if (!re.test(v)) throw new Error(`Email non valida: ${v}`);
      const k = v.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k); out.push(v);
    }
    return out;
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!nome.trim()) return setError("Il nome dell'appalto è obbligatorio");
    const ids = [...selected];
    let refEmails;
    try { refEmails = cleanEmails(); } catch (err) { return setError(err.message); }
    setSaving(true);
    try {
      if (!isEdit) {
        await api.post("/admin/appalti", { nome, descrizione, servizio_ids_abilitati: ids, email_riferimento: refEmails });
        toast.success("Appalto creato");
      } else {
        await api.put(`/admin/appalti/${appalto.id}`, { nome, descrizione, email_riferimento: refEmails });
        const before = new Set(appalto.servizio_ids_abilitati || []);
        const changed = ids.length !== before.size || ids.some((i) => !before.has(i));
        if (changed) await api.patch(`/admin/appalti/${appalto.id}/servizi`, { servizio_ids_abilitati: ids });
        toast.success("Appalto aggiornato");
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="appalto-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Modifica" : "Nuovo"} appalto</DialogTitle>
          <DialogDescription>Seleziona i servizi di sistema disponibili in questo appalto.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="appalto-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="space-y-1.5"><Label>Nome</Label><Input value={nome} data-testid="appalto-nome-input" onChange={(e) => setNome(e.target.value)} placeholder="Es. Trezzano sul Naviglio" /></div>
          <div className="space-y-1.5"><Label>Descrizione (opz.)</Label><Textarea value={descrizione} data-testid="appalto-descrizione-input" onChange={(e) => setDescrizione(e.target.value)} /></div>
          <div className="space-y-2">
            <Label>Email di riferimento (opz.)</Label>
            <p className="text-xs text-slate-500 -mt-1">Uno o più recapiti specifici di questo appalto (usati in futuro per invii/comunicazioni).</p>
            <div className="space-y-2" data-testid="appalto-emails-list">
              {emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input type="email" value={em} data-testid={`appalto-email-input-${i}`} onChange={(e) => setEmailAt(i, e.target.value)} placeholder="referente@comune.it" />
                  <Button type="button" variant="ghost" size="icon" className="shrink-0 text-slate-400 hover:text-destructive" data-testid={`appalto-email-remove-${i}`} onClick={() => removeEmail(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="rounded-lg" data-testid="appalto-email-add" onClick={addEmail}>
              <Plus className="h-4 w-4 mr-1" /> Aggiungi email
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Servizi abilitati</Label>
            <div className="rounded-lg border border-slate-200 divide-y" data-testid="appalto-services-list">
              {services.map((s) => (
                <label key={s.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`appalto-service-${s.slug}`} />
                  <span className="text-sm font-medium text-slate-700">{serviceDisplayName(s)}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="appalto-save-btn" className="w-full rounded-lg h-11 font-semibold">
              {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea appalto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
